import React, { useCallback, useEffect, useState } from 'react';
import {
  getHeaderValue,
  ALL_MODULES,
  getDownloadURL,
  EventEmitter,
  Loader,
  toast,
  fetchApi,
  isAuthorised,
  AppContext,
  EventEmitterWithIndex,
  getTranslatedText
} from '@nile/components';
import { withErrorBoundary } from '@nile/components/src/HOC';
import Tribute from 'tributejs';
import 'tributejs/src/tribute.scss';
import './CustomAttachmentsPlugin';
import envConfig from '@nile/home/src/Config/env';
import { injectIntl, useIntl } from 'react-intl';
import { getSubjectPlaceholdersObj } from '@nile/banyan/src/Pebbles/subjectPlaceholders';
import {
  getCurrentUserHook,
  restructureHtml,
  useIsAuthorisedHook
} from '@nile/components/src/Utils/helpers';
// Require Editor JS files.
import 'froala-editor/js/froala_editor.pkgd.min';
import 'froala-editor/js/plugins/image.min';
import 'froala-editor/js/plugins/file.min';
import 'froala-editor/js/plugins/files_manager.min';
import 'froala-editor/js/plugins/font_family.min';
import 'froala-editor/js/plugins/colors.min';
import 'froala-editor/js/plugins/font_size.min';
import 'froala-editor/js/plugins/paragraph_format.min';
import 'froala-editor/js/plugins/paragraph_style.min';
import 'froala-editor/js/plugins/lists.min';
import 'froala-editor/js/plugins/align.min';
import 'froala-editor/js/plugins/emoticons.min';
import 'froala-editor/js/plugins/link.min';
import 'froala-editor/js/plugins/table.min';

// Require Editor CSS files.
import 'froala-editor/css/froala_style.min.css';
import 'froala-editor/css/froala_editor.pkgd.min.css';
import 'froala-editor/css/plugins/emoticons.min.css';

// Require Font Awesome.
// import 'font-awesome/css/font-awesome.css';

import styled from 'styled-components';
import FroalaEditor from 'react-froala-wysiwyg';
import { cloneDeep, isEmpty, isEqual } from 'lodash';
import CustomPromptModal from '@nile/components/src/Modals/CustomPromptModal';
import { Link } from '@superops/ui-design-system';
import { AccountClient, GET_ALL_TECHS_NEW } from '@nile/graphql';
import { useSuperEditor } from './useSuperEditor';

// Include special components if required.
// import FroalaEditorView from 'react-froala-wysiwyg/FroalaEditorView';
// import FroalaEditorA from 'react-froala-wysiwyg/FroalaEditorA';
// import FroalaEditorButton from 'react-froala-wysiwyg/FroalaEditorButton';
// import FroalaEditorImg from 'react-froala-wysiwyg/FroalaEditorImg';
// import FroalaEditorInput from 'react-froala-wysiwyg/FroalaEditorInput';

// Render Froala Editor component.

const DEFAULT_TEMPLATE = {
  name: '',
  content: '',
  placeholdersUsed: '',
  mentions: [],
  attachments: [],
  inlineImages: []
};

const AttachmentsHolder = styled.div`
  gap: 10px;
  height: ${(props) => (props.hasAttachments ? '65px' : '0px')};
  overflow: scroll;

  // border: 1px solid #ccc;
  border: none;
  border-top: none;
  padding: ${(props) => (props.hasAttachments ? '5px 5px 5px 0' : '0px')};
`;

const EditorWrapper = styled.div`
  min-height: inherit;

  .fr-box.fr-basic {
    display: flex;
    flex-direction: column;
    border-radius: 0 !important;
    transition: height 0.5s ease-in 0.1s;
    min-height: inherit;
    height: ${(props) =>
      props.hasAttachments ? `calc(100% - 65px) !important` : `100%`};

    .fr-wrapper {
      background: transparent;
      flex-grow: 1;
      height: calc(100% - 50px);
      border: none;
      border-radius: 0 !important;
      transition: height 0.5s ease-in 0.5s;
      .fr-view {
        padding: 0 0 20px;
      }
      &.show-placeholder .fr-placeholder {
        font-size: 13px !important;
        font-style: italic !important;
        color: rgba(0, 0, 0, 0.6) !important;
      }
    }
    .fr-more-toolbar {
      margin-top: -48px;
      width: auto;
      padding-right: 12px;
      border-radius: 10px;
    }
    &.fr-toolbar-open .fr-wrapper {
      // height: calc(100% - 100px) !important;
    }

    .fr-toolbar {
      background: transparent;
    }
    .fr-toolbar.fr-bottom {
      border: none !important;
      .fr-newline {
        display: none;
      }
    }
  }
`;

export const AttachmentItemHolder = styled.div`
  display: inline-flex;
  background-color: #f2f2f7;
  border-radius: 8px;
  /* margin: 1rem; */
  margin-top: 0;
  word-break: break-word;
  .Polaris-Link {
    display: flex;
    align-items: center;
    padding: 1rem;
    padding-right: 0.2rem;
  }
  &:hover {
    cursor: pointer;
  }
  .close-action {
    border-radius: 0 8px 8px 0;
    padding: 1rem;
    display: flex;
    align-items: center;
    &:hover {
      background-color: #ffbdad;
      color: #de350b;
    }
  }
`;

const AttachmentItem = ({
  fileName,
  originalFileName,
  handleAttachmentsDeletion,
  template,
  module: parentModule,
  disableDownload,
  takeSnapshot
}) => {
  const module = parentModule || ALL_MODULES.notificationTemplateAttachment;
  const details = {
    fileName: fileName
  };

  switch (module) {
    case ALL_MODULES.notificationTemplateAttachment:
      details.templateId = template?.id;
      details.type = template?.templateType;
      break;
    case 'PROJECT_TEMPLATE_ATTACHMENT':
      details.projectTemplateId = template?.id;
      break;
    default:
      break;
  }
  const url = getDownloadURL({ module, details });

  return (
    <AttachmentItemHolder>
      <Link removeUnderline url={!disableDownload && url}>
        {originalFileName}
      </Link>
      <div
        className="close-action"
        onClick={() => handleAttachmentsDeletion(fileName)}
      >
        x
      </div>
    </AttachmentItemHolder>
  );
};

const SuperEditor = (props) => {
  let editorFileOption = ['insertLink'];
  const intl = useIntl();

  const { template, updateTemplate } = useSuperEditor(props?.storeConfig);

  const editorRef = React.useRef(null);

  const focusTimeOutRef = React.useRef(null);

  const imagesLoadingRef = React.useRef(null);

  const initialFocusTimeOutRef = React.useRef(null);

  imagesLoadingRef.current = [];

  if (!props.disableAttachment) {
    editorFileOption = [...editorFileOption, 'attachFile'];
  }
  if (!props.disableImageUpload) {
    editorFileOption = [...editorFileOption, 'insertImage'];
  }
  if (props.enableScreenShot) {
    editorFileOption = [...editorFileOption, 'takeSnapshot'];
  }

  const handleScreenshot = () => {
    if (props.takeSnapshot) {
      props.takeSnapshot();
    }
  };

  const additionalConfig = props?.config || {};
  const [technicians, setTechnicians] = useState([]);

  const mentionsRef = React.useRef(template?.mentions || null);
  const placeholdersRef = React.useRef(template?.placeholdersUsed || null);

  const collections = [
    {
      trigger: '@',
      lookup: 'key',
      values: technicians,
      selectTemplate: ({ original }) => {
        const mentionedUser = original?.value;
        let updatedMentions = cloneDeep(mentionsRef.current);

        if (updatedMentions) {
          const isUserAlreadyMentioned = updatedMentions.find(
            (ment) => ment?.userId === mentionedUser
          );
          if (!isUserAlreadyMentioned) {
            updatedMentions.push({ userId: mentionedUser });
          }
        } else {
          updatedMentions = [{ userId: mentionedUser }];
        }

        mentionsRef.current = updatedMentions;

        return `<span data-denotation-char="@" data-id=${original?.value} data-value=${original?.key} class="fr-deletable fr-tribute mention" contenteditable="false" >@${original.key}</span>`;
      }
    },
    {
      trigger: '#',
      lookup: 'display',
      fillAttr: 'display',
      menuItemTemplate: ({ original }) => {
        return original?.display;
      },
      selectTemplate: ({ original }) => {
        const usedPlaceHolder = original;
        const placeholdersData = cloneDeep(placeholdersRef.current);
        const updatedPlaceholders = placeholdersData
          ? JSON.parse(placeholdersData)
          : { content: '' };

        if (updatedPlaceholders) {
          if (!updatedPlaceholders.content) {
            updatedPlaceholders.content = {};
          }
          if (!updatedPlaceholders.content[usedPlaceHolder?.id]) {
            updatedPlaceholders.content[usedPlaceHolder.id] =
              usedPlaceHolder.value;
          }
        }

        placeholdersRef.current = updatedPlaceholders;

        return `<span data-denotation-char="#" data-id="${original?.id}" data-value="${original?.value}" class="fr-deletable fr-tribute mention" contenteditable="false">#${original.value}</span>`;
      },
      values: []
    }
  ];

  const tribute = new Tribute({
    menuContainer: document.body,
    collection: collections.filter((coll) => {
      if (coll?.trigger === '@') {
        return !!props?.allowMentions;
      }
      if (coll?.trigger === '#') {
        return !!props?.allowPlaceholders;
      }
      return true;
    })
  });

  const config = {
    app: 'SuperOps',
    docId: 'mydoc',
    username: 'Superops.ai',
    key:
      'jUA1yC3C4D2E2G2B2E1qYFa1UQRFQIVc2MSMd1IWPNb1IFd1yD2I2D1C2C7E2C1C5G1F1==',
    heightMin: 200,
    placeholderText: props.placeholder || null,
    pluginsEnabled: [
      'align',
      'charCounter',
      'codeBeautifier',
      'codeView',
      'colors',
      'draggable',
      'embedly',
      'emoticons',
      'entities',
      'file',
      'fontAwesome',
      'fontFamily',
      'fontSize',
      'fullscreen',
      'image',
      'imageTUI',
      'imageManager',
      'filesManager',
      'inlineStyle',
      'inlineClass',
      'lineBreaker',
      'lineHeight',
      'link',
      'lists',
      'paragraphFormat',
      'paragraphStyle',
      'quickInsert',
      'quote',
      'save',
      'table',
      'url',
      'video',
      'wordPaste',
      'customPlugin'
    ],
    toolbarBottom: true,
    toolbarButtons: {
      moreText: {
        buttons: [
          'bold',
          'italic',
          'underline',
          'fontFamily',
          'fontSize',
          'textColor',
          'backgroundColor',
          'inlineClass',
          'inlineStyle',
          'clearFormatting'
        ]
      },
      moreTable: {
        buttons: ['insertTable']
      },
      moreParagraph: {
        buttons: [
          'alignLeft',
          'alignCenter',
          'alignRight',
          'alignJustify',
          'formatOL',
          'formatUL',
          'paragraphFormat',
          'quote'
        ],
        buttonsVisible: 0
      },
      moreRich: {
        buttons: editorFileOption,
        buttonsVisible: 4
      },
      moreMisc: {
        buttons: ['emoticons', 'undo', 'redo'],
        buttonsVisible: 3
      }
    },
    tableEditButtons: [
      'tableHeader',
      'tableRemove',
      'tableRows',
      'tableColumns',
      'tableCells',
      'tableCellBackground',
      'tableCellVerticalAlign',
      'tableCellHorizontalAlign'
    ],
    imagePaste: !props.disableImageUpload,
    linkList: [],
    linkInsertButtons: [],
    emoticonsUseImage: false,
    tabSpaces: 4,
    fontSizeSelection: true,
    paragraphFormatSelection: true,
    imageDefaultAlign: 'center',
    imageEditButtons: ['imageAlign'],
    imageUpload: true,
    useClasses: false,
    imageDefaultDisplay: 'inline',
    toolbarSticky: props.toolbarSticky,
    charCounterCount: props.showCharacterCount || false,
    events: {
      'commands.after': (cmd) => {
        if (cmd === 'screenshot') {
          handleScreenshot();
        }
      },
      blur: function() {
        editorRef.current?.selection.save();
      },
      focus: function() {
        if (editorRef.current?.selection) {
          // If the user clicks on the suggestion from Microsoft suggestion
          // It replaces the whole line instead of the word.
          // Reached out to Froala support team insisted to add timeout
          if (focusTimeOutRef.current) {
            clearTimeout(focusTimeOutRef.current);
          }
          focusTimeOutRef.current = setTimeout(() => {
            editorRef.current?.selection.restore();
          }, 1);
        }
      },
      initialized: function() {
        editorRef.current = this;
        if (props?.editorRef && props?.editorRef.hasOwnProperty('current')) {
          const instance = this;
          const prevInstance = props.editorRef.current || {};
          props.editorRef.current = {
            ...prevInstance,
            FroalaEditorRef: { current: instance }
          };
        }

        if (props?.hideToolbar) {
          this.toolbar.hide();
        }
        if (props?.focusEditor) {
          const focusEnd = !props?.focusStart;
          if (focusEnd) {
            this.selection.setAtEnd(this.el);
          } else {
            this.selection.setAtStart(this.el);
          }
          this.selection.restore();
        }

        if (tribute && (props?.allowMentions || props?.allowPlaceholders)) {
          tribute.attach(this.el);

          this.events.on(
            'keydown',
            function(e) {
              // Check if the menu is open and visible
              const menuVisible = tribute.menu?.style.display === 'block';

              if (menuVisible && (e.key === 'Enter' || e.key === 'Tab')) {
                e.preventDefault();
                e.stopPropagation();
                // Trigger Tribute's own select logic
                if (tribute.menuSelected >= 0) {
                  tribute.selectItemAtIndex(tribute.menuSelected);
                } else {
                  // If nothing is highlighted, select the first item
                  tribute.selectItemAtIndex(0);
                }
                tribute.hideMenu();
                return false;
              }

              if (menuVisible && e.key === 'Escape') {
                e.preventDefault();
                tribute.hideMenu();
                return false;
              }
              return true;
            },
            true
          );
        }
      },
      'image.beforeUpload': function(images) {
        editorRef.current?.image.showProgressBar();
        const uploadImage = (image, imageInstance = null) => {
          const formData = new FormData();
          formData.append('image', image);
          formData.append('module', props.module);
          formData.append('details', JSON.stringify(props.details));

          const requestOptions = {
            method: 'POST',
            credentials: 'include',
            body: formData,
            headers: getHeaderValue()
          };

          const getModuleURL = () => {
            switch (module) {
              default:
                return `${envConfig.fileRoot}/inline_images`;
            }
          };
          EventEmitter.dispatch('imageUploading', true);

          fetchApi(getModuleURL(), requestOptions)
            .then((response) => {
              return response.json();
            })
            .then((result) => {
              if (result?.data?.link) {
                const url = result.data.link;
                editorRef.current?.image.insert(
                  url,
                  false,
                  null,
                  imageInstance,
                  result
                );
                console.log(result);
              } else {
                editorRef.current?.image.remove(imageInstance);
                toast('error', 'Upload failed');
              }
              return false;
            })
            .catch((error) => {})
            .finally(() => EventEmitter.dispatch('imageUploading', false));
        };

        if (images.length > 1) {
          images.forEach((image) => {
            imagesLoadingRef.current.push(image);
            uploadImage(image);
          });
        } else {
          imagesLoadingRef.current.push(images[0]);
          const currentImage = editorRef.current?.image.get();
          uploadImage(images[0], currentImage);
        }
        return false;
      },
      'image.loaded': function($img) {
        if (imagesLoadingRef.current.length === 1) {
          EventEmitter.dispatch('imageUploading', false);
        }
        if (imagesLoadingRef.current.length) {
          imagesLoadingRef.current.pop();
        }
      },
      'image.removed': function($img) {
        // Do something here.
        // this is the editor instance.
        console.log('pvs:: removed', this, $img);
      },
      'image.uploaded': function(response) {
        // Do something here.
        // this is the editor instance.
        console.log('uploaded:: ', this, response);
      }
    },
    ...additionalConfig
  };

  const model = template?.content || '';
  const placeholdersUsed = template?.placeholdersUsed || '{}';
  const mentions = template?.mentions || [];
  const attachments = template?.attachments || [];

  const [attachmentsModalOpen, setAttachmentsModalOpen] = useState(false);
  const [addAttachmentsLoading, setAddAttachmentsLoading] = useState(false);

  const currentUser = getCurrentUserHook();
  const isTechAuthorised = useIsAuthorisedHook('employees.view');

  const openAttachmentsModal = () => {
    setAttachmentsModalOpen(true);
  };

  const handleFileAttachments = async (files) => {
    try {
      setAddAttachmentsLoading(true);
      if (props?.setIsUploading) {
        props?.setIsUploading(true);
      }
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });

      const uploadParams = props?.uploadParams;

      Object.entries(uploadParams).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const requestOptions = {
        method: 'POST',
        credentials: 'include',
        body: formData,
        headers: {
          ...getHeaderValue()
        }
      };

      const response = await fetchApi(
        props?.attachmentUploadUrl,
        requestOptions
      );
      const data = await response.json();

      const list = data?.data || [];
      const currentTemplate = template ? cloneDeep(template) : DEFAULT_TEMPLATE;
      const currentAttachments = currentTemplate?.attachments || [];
      const updatedList = [...currentAttachments, ...list];
      const newTemplate = { ...currentTemplate, attachments: updatedList };
      updateTemplate(newTemplate);
    } catch (error) {
      toast('error', error?.message || 'Upload failed');
      console.error('Error:', error);
    } finally {
      setAddAttachmentsLoading(false);
      if (props?.setIsUploading) {
        props?.setIsUploading(false);
      }
    }
  };

  const handleAttachmentsDeletion = (fileName) => {
    const currentAttachments = template?.attachments;
    if (currentAttachments?.length) {
      const updatedList = cloneDeep(template?.attachments).filter(
        (item) => item.fileName !== fileName
      );
      updateTemplate({ ...template, attachments: updatedList });
    }
  };

  const insertText = (textContent) => {
    if (!editorRef.current) {
      console.warn('Editor not ready yet');
      return;
    }

    try {
      editorRef.current.selection.restore();
      editorRef.current.html.insert(textContent);
    } catch (e) {
      console.error('Failed to insert text:', e);
    }
  };

  const insertTextOnly = (textContent) => {
    if (editorRef.current && typeof textContent === 'string') {
      try {
        setTimeout(() => {
          editorRef.current.html.insert(textContent);
        }, 100);
      } catch (e) {}
    }
  };

  const replaceContent = (newContent) => {
    if (newContent) {
      editorRef.current.html.set(newContent);
      if (updateTemplate) {
        updateTemplate({ content: newContent });
      }
    }
  };

  const getUpdatedMentions = (updatedModel) => {
    const updatedMentions = cloneDeep(mentionsRef.current);
    if (!updatedMentions?.length) {
      return [];
    }
    const dummyDiv = document.createElement('div');
    dummyDiv.innerHTML = updatedModel;
    const mentionedItems = Array.from(
      new Set(
        Array.from(
          dummyDiv.querySelectorAll('span[data-id][data-denotation-char="@"]')
        ).map((item) => item.getAttribute('data-id'))
      )
    );
    const finalMentions = updatedMentions.filter((item) =>
      mentionedItems.includes(item?.userId)
    );
    return finalMentions;
  };

  const getUpdatedPlaceholders = (updatedModel) => {
    const updatedPlaceholders = cloneDeep(placeholdersRef.current);

    const dummyDiv = document.createElement('div');
    dummyDiv.innerHTML = updatedModel;
    updatedPlaceholders.content = Array.from(
      dummyDiv.querySelectorAll('span[data-id][data-denotation-char="#"]')
    ).reduce((acc, currentNode) => {
      const placeholderKey = currentNode.getAttribute('data-id');

      if (!placeholderKey) return acc;

      if (placeholderKey.includes('udf')) {
        acc[placeholderKey] = currentNode.getAttribute('data-value');
        return acc;
      }

      acc[placeholderKey] = getTranslatedText(intl, placeholderKey);
      return acc;
    }, {});

    return updatedPlaceholders;
  };

  const handleModelChange = (updatedModel) => {
    let updatedMentions = null;
    let updatedPlaceholdersUsed = null;

    const updatedTemplate = {
      content: updatedModel
    };

    if (props?.allowMentions) {
      updatedMentions = getUpdatedMentions(updatedModel);
      updatedTemplate.mentions = updatedMentions;
    }

    if (props?.allowPlaceholders) {
      updatedPlaceholdersUsed = getUpdatedPlaceholders(updatedModel);
      updatedTemplate.placeholdersUsed = updatedPlaceholdersUsed;
    }

    updateTemplate(updatedTemplate);

    if (props?.onChange) {
      props?.onChange(updatedTemplate);
    }
  };

  const getUsersForMention = () => {
    if (isTechAuthorised) {
      AccountClient.query({
        query: GET_ALL_TECHS_NEW,
        variables: {
          input: {
            listInfo: {
              sort: {
                attribute: 'name',
                order: 'ASC'
              }
            }
          }
        }
      }).then((res) => {
        if (res?.data?.getTechnicianList?.userList) {
          const allTechs = res.data.getTechnicianList.userList.map((tech) => ({
            ...tech,
            key: tech.name,
            value: tech.userId
          }));

          setTechnicians(allTechs);
          tribute.collection[0].values = allTechs;
        }
      });
    }
  };

  const translatedMentionedPlaceholders = () => {
    const mentionedPlaceholders = [];

    const currentMentions = document.querySelector(
      'span.mention[data-id][data-denotation-char="#"]'
    );
    const nodes = currentMentions?.children || [];

    const hasMentionDenotationChar = Array.from(nodes).some((child) =>
      child.classList.contains('ql-mention-denotation-char')
    );

    return Array.from(
      document.querySelectorAll(
        'span.mention[data-id][data-denotation-char="#"]'
      )
    ).reduce((acc, node) => {
      const localeId = node.getAttribute('data-id');
      node.setAttribute('contenteditable', 'false');

      const translatedValue = getTranslatedText(intl, localeId);

      /* Replacing the placeholders resolved values with current selected lang */

      const selector = hasMentionDenotationChar
        ? node.childNodes
        : node.querySelector('span').childNodes;

      // eslint-disable-next-line no-restricted-syntax
      for (const subNode of Array.from(selector)) {
        if (subNode.nodeName === '#text') {
          // eslint-disable-next-line no-param-reassign
          if (localeId.includes('udf')) {
            // Skip for custom field
            if (!acc[localeId]) {
              mentionedPlaceholders.push(localeId);
            }
            acc[localeId] = subNode.textContent;
            return acc;
          }
          subNode.textContent = translatedValue;
        }
      }

      if (node.getAttribute('data-value')) {
        node.setAttribute('data-value', translatedValue);
      }
      if (!acc[localeId]) {
        mentionedPlaceholders.push(localeId);
      }
      acc[localeId] = translatedValue;
      return acc;
    }, {});
  };

  const translatedMentionedPlaceholdersWrapper = () => {
    const currentPlaceHolders = cloneDeep(template?.placeholdersUsed);
    const updatedPlaceholders = JSON.parse(currentPlaceHolders || '{}');

    if (!updatedPlaceholders.content) {
      updatedPlaceholders.content = {};
    }

    const translatedMap = translatedMentionedPlaceholders();

    const prevContent = updatedPlaceholders.content || {};
    const same =
      Object.keys(translatedMap).length === Object.keys(prevContent).length &&
      Object.keys(translatedMap).every(
        (key) => translatedMap[key] === prevContent[key]
      );

    if (same) {
      return; // nothing changed, skip updateTemplate
    }

    const currentTemplate = {
      placeholdersUsed: JSON.stringify({
        content: translatedMap
      })
    };

    updateTemplate(currentTemplate);
  };

  useEffect(() => {
    if (!isEmpty(template?.placeholdersUsed)) {
      translatedMentionedPlaceholdersWrapper();
    }
  }, [template?.placeholdersUsed]);

  useEffect(() => {
    if (props?.allowMentions) {
      getUsersForMention();
    }

    EventEmitter.subscribe('openCustomAttachments', openAttachmentsModal);
    EventEmitter.subscribe('insertOnlyEditorContent', insertTextOnly);
    EventEmitter.subscribe('insertEditorContent', insertText);
    const insertEventEmitterIndex1 = EventEmitterWithIndex.subscribe(
      'insertOnlyEditorContent',
      insertTextOnly
    );
    const insertEventEmitterIndex2 = EventEmitterWithIndex.subscribe(
      'insertEditorContent',
      insertText
    );
    const replaceEventEmitterIndex = EventEmitterWithIndex.subscribe(
      'replaceEditorContent',
      replaceContent
    );

    return function cleanup() {
      EventEmitter.unsubscribe('openCustomAttachments');
      EventEmitter.unsubscribe('insertOnlyEditorContent');
      EventEmitter.unsubscribe('insertEditorContent');
      EventEmitterWithIndex.unsubscribe(
        'insertOnlyEditorContent',
        insertEventEmitterIndex1
      );
      EventEmitterWithIndex.unsubscribe(
        'insertEditorContent',
        insertEventEmitterIndex2
      );
      EventEmitterWithIndex.unsubscribe(
        'replaceEditorContent',
        replaceEventEmitterIndex
      );
    };
  }, []);

  useEffect(() => {
    const show = !props?.hideToolbar;
    if (editorRef?.current) {
      if (show) {
        editorRef?.current?.toolbar.show();
      } else {
        editorRef?.current?.toolbar.hide();
      }
    }
  }, [props?.hideToolbar]);

  return (
    <EditorWrapper
      hasAttachments={addAttachmentsLoading || attachments?.length > 0}
      className="full-height"
    >
      <FroalaEditor
        tag="textarea"
        config={config}
        model={model}
        onModelChange={handleModelChange}
      />
      {addAttachmentsLoading && !attachments?.length && (
        <AttachmentsHolder className="flex wrap" hasAttachments={1}>
          <Loader />
        </AttachmentsHolder>
      )}
      {attachments && (
        <AttachmentsHolder
          className="flex wrap"
          hasAttachments={attachments?.length > 0}
        >
          {attachments.map((attachment) => (
            <AttachmentItem
              fileName={attachment?.fileName}
              originalFileName={attachment?.originalFileName}
              fileSize={attachment?.fileSize}
              handleAttachmentsDeletion={handleAttachmentsDeletion}
              template={template}
              module={props?.module}
              disableDownload={
                props?.module !== 'NOTIFICATION_TEMPLATE' ||
                props?.module !== 'TICKET_NOTIFICATION_TEMPLATE' ||
                props?.module !== 'ON_BOARDING_NOTIFICATION_TEMPLATE' ||
                props?.module !== 'REPLY_TEMPLATE' ||
                module !== 'PROJECT_TEMPLATE'
              }
            />
          ))}
        </AttachmentsHolder>
      )}
      <CustomPromptModal
        allowMultiple={!props?.singleFile}
        title={props?.title || 'Add Attachments'}
        description={props?.description || 'Upload/drop files here'}
        active={attachmentsModalOpen}
        setActive={(val) => {
          setAttachmentsModalOpen(val);
        }}
        handleSubmit={handleFileAttachments}
        acceptType={props?.acceptType} // For single select to accept only given file types
        acceptAllType={props?.acceptAllType} // For single select to accept all type
      />
    </EditorWrapper>
  );
};

export default withErrorBoundary(SuperEditor);
